/**
 * P-3 日報詳細・入力 (§14-2/§14-3-A)。
 * 値入力・明細行・添付導線・提出・(提出後は)突合結果の確認と受け入れを行う中心画面。
 * バックエンド: apps/api/src/routes/daily-reports.ts。設計: docs/design/v05-daily-report-detail.md §14-3-A/C。
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Loader2, Camera, Trash2 } from 'lucide-react';
import { Button } from '@/components/Button';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/stores/toast';
import { ApiClientError } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import {
  fetchDailyReportDetail, fetchDenominations, fetchDailyReportReconciliationReasons,
  submitDailyReport, acceptDailyReportReconciliation, parseLineItemFieldOptions,
  inferAttachmentRequiredCount, deleteDailyReportAttachment, reopenDailyReport,
  type DailyReportField, type DailyReportFieldValue, type DailyReportFormResponse,
  type DailyReportCarryover, type DailyReportLineItemRow, type DailyReportAttachment,
  type DailyReportReconciliation, type DailyReportHeader, type Denomination,
} from '@/lib/daily-report-api';
import { useDailyReportAutosave } from './useDailyReportAutosave';
import { DailyReportFieldRow, inputCls } from './DailyReportFieldRow';
import { DenominationLineItemsSection, GeneralLineItemsSection } from './DailyReportLineItems';
import { DailyReportWorkShiftsSection } from './DailyReportWorkShifts';

type SubmitErrorDetail = { field?: string; code?: string; message: string };

const todayLocalDateString = () => {
  // ⚠️ node の business_day_start_time (§5) までは考慮しない簡略化。ブラウザのローカル暦日で
  // 近似する (店長が今見ている「今日」の直感と一致させる目的、正確な営業日境界はサーバー側 GET /form が権威)。
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatBusinessDateLabel = (dateStr: string) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return dateStr;
  const dt = new Date(y, m - 1, d);
  const weekday = new Intl.DateTimeFormat('ja-JP', { weekday: 'short' }).format(dt);
  return `${y}年${m}月${d}日(${weekday})`;
};

const STATUS_LABEL: Record<string, string> = { draft: '下書き', submitted: '提出済み', approved: '承認済み' };
/** 安定した参照の空配列 (毎回 `?? []` すると useSyncExternalStore の無限ループを招く、実機で発見)。 */
const EMPTY_ROLES: string[] = [];

export function DailyReportDetailPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const detailQuery = useQuery({
    queryKey: ['daily-report-detail', id],
    queryFn: () => fetchDailyReportDetail(id!),
    enabled: !!id,
    staleTime: Infinity, // ローカル state を唯一の表示ソースにする (バックグラウンド再取得で編集中の値を上書きしない)
  });
  const denominationsQuery = useQuery({ queryKey: ['denominations'], queryFn: fetchDenominations });
  const reasonsQuery = useQuery({ queryKey: ['daily-report-reconciliation-reasons'], queryFn: fetchDailyReportReconciliationReasons });

  const [header, setHeader] = useState<DailyReportHeader | null>(null);
  const [fields, setFields] = useState<DailyReportField[] | null>(null);
  const [lineItemsByField, setLineItemsByField] = useState<Map<string, DailyReportLineItemRow[]>>(new Map());
  const [attachments, setAttachments] = useState<DailyReportAttachment[]>([]);
  const [reconciliations, setReconciliations] = useState<DailyReportReconciliation[]>([]);
  const carryoverHintsRef = useRef<Map<string, DailyReportCarryover>>(new Map());

  const autosave = useDailyReportAutosave(id ?? '', header?.row_version ?? 1);
  const [submitErrors, setSubmitErrors] = useState<SubmitErrorDetail[]>([]);
  const [submitting, setSubmitting] = useState(false);
  // user指摘 (2026-09-09): 「提出する」ワンクリックで即確定していたのを、内容一覧を見せてから
  // 最終確認するフローに変更。プレビューは既存の画面表示ロジック (DailyReportFieldRow disabled 等)
  // をモーダル内で再利用し、専用の描画ロジックを新設しない (§8-134型のミラーずれ回避)。
  const [showSubmitPreview, setShowSubmitPreview] = useState(false);
  // 2026-09-09 (user Q1): 提出済み日報の差し戻し。company_adminのみ (バックエンドが強制、
  // フロントは表示制御のみ)。FeaturesSettingsPage.tsx と同じ判定パターン。
  // ⚠️ `s.user?.roles ?? []` はセレクタが毎回新しい配列を返し、useSyncExternalStore の
  // snapshot不一致を誘発して無限レンダーループを起こした (実機で発見・是正)。`s.user` (store側で
  // 安定した参照) を選択し、フォールバックは呼び出しごとに作らないモジュールスコープ外の判定にする。
  const authUser = useAuthStore((s) => s.user);
  const roles = authUser?.roles ?? EMPTY_ROLES;
  const canReopen = roles.includes('platform_admin') || roles.includes('company_admin');
  const [reopenModalOpen, setReopenModalOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [reopening, setReopening] = useState(false);
  // design-expert/QAレビュー CRITICAL是正 (2026-09-09): 複数枚添付の個別削除はクリック即実行だと
  // 誤タップで写真とOCR結果を失う (再撮影・再読取のやり直しが必要な高コスト操作)。
  // 既存の設定画面削除 (ConfirmDialog) と同じ確認ステップを挟む。
  const [deleteAttachmentTarget, setDeleteAttachmentTarget] = useState<string | null>(null);
  const [deletingAttachment, setDeletingAttachment] = useState(false);
  const [ocrOverrideTarget, setOcrOverrideTarget] = useState<{ field: DailyReportField; value: DailyReportFieldValue; isNotApplicable: boolean } | null>(null);
  const [ocrOverrideReason, setOcrOverrideReason] = useState('');
  const fieldRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // 初回ロード: GET /:id を唯一の表示ソースにする。P-2 から渡された formResponse (§4-1-1 の
  // carryover.days_ago) はキャプション強化用のヒントとしてのみ使う (再現できない既知の簡略化)。
  useEffect(() => {
    if (!detailQuery.data || fields !== null) return;
    const stateForm = (location.state as { formResponse?: DailyReportFormResponse } | undefined)?.formResponse;
    if (stateForm && stateForm.daily_report.id === id) {
      for (const f of stateForm.fields) {
        if (f.carryover) carryoverHintsRef.current.set(f.field_definition_id, f.carryover);
      }
    }
    setHeader(detailQuery.data.daily_report);
    setFields(detailQuery.data.fields);
    setLineItemsByField(new Map(detailQuery.data.line_items.map((g) => [g.field_definition_id, g.rows])));
    setAttachments(detailQuery.data.attachments);
    setReconciliations(detailQuery.data.reconciliations);
    autosave.setRowVersion(detailQuery.data.daily_report.row_version);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 初回ハイドレートのみ (fields===null ガードで1回化)
  }, [detailQuery.data]);

  const reloadFromServer = async () => {
    if (!id) return;
    const fresh = await fetchDailyReportDetail(id);
    setHeader(fresh.daily_report);
    setFields((prev) => fresh.fields.map((f) => {
      const local = prev?.find((p) => p.field_definition_id === f.field_definition_id);
      // 未送信のキューが残っているフィールドはローカル値を優先する (§14-3-A「非破壊的」)。
      return autosave.isFieldQueued(f.field_definition_id) && local ? local : f;
    }));
    setLineItemsByField((prev) => {
      const next = new Map(fresh.line_items.map((g) => [g.field_definition_id, g.rows]));
      for (const [fid, rows] of prev) if (autosave.isLineItemsQueued(fid)) next.set(fid, rows);
      return next;
    });
    setAttachments(fresh.attachments);
    setReconciliations(fresh.reconciliations);
    autosave.setRowVersion(fresh.daily_report.row_version);
  };

  // 2026-09-09 (user Q3、複数枚添付の個別削除): 削除頻度が低い操作のため、既存の
  // reloadFromServer (全体再取得) を再利用する。専用の部分更新パスを新設して§8-127型
  // (配線をN箇所に分散させて漏らす) の穴を増やさない。
  const handleDeleteAttachment = async () => {
    if (!id || !deleteAttachmentTarget) return;
    setDeletingAttachment(true);
    try {
      await deleteDailyReportAttachment(id, deleteAttachmentTarget);
      await reloadFromServer();
      toast.success('削除しました');
      setDeleteAttachmentTarget(null);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : '削除に失敗しました');
    } finally {
      setDeletingAttachment(false);
    }
  };

  useEffect(() => {
    autosave.setOnConflict(reloadFromServer);
    return () => autosave.setOnConflict(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const isDraft = header?.status === 'draft';
  const isNotToday = header?.business_date != null && header.business_date !== todayLocalDateString();

  const handleFieldChange = (field: DailyReportField, value: DailyReportFieldValue, isNotApplicable: boolean) => {
    if (field.source === 'ocr') {
      setOcrOverrideTarget({ field, value, isNotApplicable });
      setOcrOverrideReason('');
      return;
    }
    commitFieldChange(field, value, isNotApplicable, undefined);
  };

  const commitFieldChange = (field: DailyReportField, value: DailyReportFieldValue, isNotApplicable: boolean, reason: string | undefined) => {
    setFields((prev) => prev?.map((f) => (f.field_definition_id === field.field_definition_id
      ? { ...f, value: isNotApplicable ? null : value, is_not_applicable: isNotApplicable, source: 'manual' }
      : f)) ?? prev);
    autosave.queueValue({ field_definition_id: field.field_definition_id, value, is_not_applicable: isNotApplicable, reason });
  };

  const confirmOcrOverride = () => {
    if (!ocrOverrideTarget) return;
    if (!ocrOverrideReason.trim()) { toast.error('理由を入力してください'); return; }
    commitFieldChange(ocrOverrideTarget.field, ocrOverrideTarget.value, ocrOverrideTarget.isNotApplicable, ocrOverrideReason.trim());
    setOcrOverrideTarget(null);
  };

  const handleSubmit = async () => {
    if (!id || !header) return;
    setSubmitErrors([]);
    setSubmitting(true);
    try {
      await autosave.flushNow();
      await submitDailyReport(id, autosave.rowVersion);
      toast.success('提出しました');
      await reloadFromServer();
      qc.invalidateQueries({ queryKey: ['daily-report-detail', id] });
      qc.invalidateQueries({ queryKey: ['daily-reports'] });
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.code === 'ROW_VERSION_MISMATCH' || err.code === 'ALREADY_SUBMITTED') {
          toast.info(err.message);
          await reloadFromServer();
        } else {
          const details = (Array.isArray(err.details) ? err.details : []) as SubmitErrorDetail[];
          setSubmitErrors(details.length > 0 ? details : [{ message: err.message }]);
          // 提出エラーサマリ (§14-1 #3): 最初の項目へ自動フォーカス
          // ⚠️ QAレビュー HIGH是正: このあと finally で setShowSubmitPreview(false) を呼ぶと
          // プレビューモーダルが閉じ、Modal.tsx のクローズ時クリーンアップが「開く前に
          // フォーカスしていた要素 (= 提出するボタン)」へ focus() を戻す。ここで同期的に
          // focus() しても、直後にそれで上書きされて無効化される。モーダルのクローズ処理
          // (React の副作用) が終わった後に実行されるよう requestAnimationFrame で1フレーム
          // 遅らせ、こちらの focus() を最後に効かせる。
          const firstFieldId = details.find((d) => d.field && fieldRefs.current.has(d.field))?.field;
          if (firstFieldId) {
            requestAnimationFrame(() => {
              const el = fieldRefs.current.get(firstFieldId);
              el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              el?.focus();
            });
          } else {
            toast.error(err.message);
          }
        }
      } else {
        toast.error('提出に失敗しました');
      }
    } finally {
      setSubmitting(false);
      // 成功時も失敗時もプレビューは閉じる (失敗時はメイン画面のエラーサマリ・該当項目への
      // 「移動」リンクで修正させるため、プレビューモーダルの中に留めない)。
      setShowSubmitPreview(false);
    }
  };

  const handleReopen = async () => {
    if (!id || !header || !reopenReason.trim()) return;
    setReopening(true);
    try {
      await reopenDailyReport(id, header.row_version, reopenReason.trim());
      toast.success('下書きに差し戻しました');
      setReopenModalOpen(false);
      setReopenReason('');
      await reloadFromServer();
      qc.invalidateQueries({ queryKey: ['daily-report-detail', id] });
      qc.invalidateQueries({ queryKey: ['daily-reports'] });
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : '差し戻しに失敗しました');
    } finally {
      setReopening(false);
    }
  };

  if (!id) return <p className="text-body text-danger-text">日報IDが指定されていません。</p>;
  if (detailQuery.isLoading || fields === null) {
    return (
      <div className="flex items-center justify-center gap-2 text-text-sub py-16">
        <Loader2 className="w-5 h-5 animate-spin" aria-hidden />
        <span className="text-body">読み込んでいます…</span>
      </div>
    );
  }
  if (detailQuery.isError || !header) {
    return <p className="text-body text-danger-text">日報の取得に失敗しました。</p>;
  }

  const renderableFields = fields.filter((f) => f.display_level === 'full');
  const maskedCount = fields.filter((f) => f.display_level === 'exists_only').length;

  const sections: { label: string | null; items: DailyReportField[] }[] = [];
  for (const f of renderableFields) {
    const last = sections[sections.length - 1];
    if (last && last.label === f.section_label) last.items.push(f);
    else sections.push({ label: f.section_label, items: [f] });
  }

  const errorByField = new Map(submitErrors.filter((d) => d.field).map((d) => [d.field!, d.message]));

  return (
    <div className="max-w-2xl mx-auto">
      <div className="sticky top-0 z-10 bg-surface-canvas/95 backdrop-blur-sm pt-2 pb-2 -mx-4 px-4 border-b border-surface-line">
        <div className="flex items-center justify-between gap-2">
          <button type="button" className="text-body text-chrome-base underline underline-offset-2 min-h-[44px] px-1 -ml-1 inline-flex items-center" onClick={() => navigate('/daily-reports')}>
            ← 一覧へ
          </button>
          <span className="flex items-center gap-2">
            <span className={`text-caption px-2 py-1 rounded-full ${header.status === 'draft' ? 'bg-surface-sunken text-text-sub' : 'bg-success-bg text-success-text'}`}>
              {STATUS_LABEL[header.status] ?? header.status}
            </span>
            {header.status === 'submitted' && canReopen && (
              // design-expertレビュー Major是正: 提出済みという確定状態を覆す破壊的操作のため、
              // 「← 一覧へ」等の無害なナビリンクと同じ控えめな意匠にしない (誤操作誘発・視認性不足)。
              <Button variant="danger-outline" size="sm" onClick={() => setReopenModalOpen(true)}>
                下書きに差し戻す
              </Button>
            )}
          </span>
        </div>
        <div className="mt-1 flex items-center justify-between gap-2 flex-wrap">
          <h1 className="text-h3 text-text-main">{header.business_date ? formatBusinessDateLabel(header.business_date) : ''}</h1>
          <input
            type="date" aria-label="営業日を切替" defaultValue={header.business_date ?? ''} max={todayLocalDateString()}
            className="text-caption border border-surface-border rounded px-2 min-h-[44px]"
            onChange={(e) => {
              if (!e.target.value || !header.node_id) return;
              navigate(`/daily-reports/open?node_id=${header.node_id}&business_date=${e.target.value}`);
            }}
          />
        </div>
      </div>

      {isNotToday && (
        <div className="mt-3 flex items-center justify-between gap-2 bg-alert-bg border border-alert-border rounded-lg px-3 py-2.5">
          <span className="text-body text-alert-text flex items-center gap-1.5"><AlertTriangle size={16} aria-hidden />今日の日報ではありません</span>
          <button type="button" className="text-caption text-alert-text underline underline-offset-2 shrink-0 min-h-[44px] px-1 inline-flex items-center"
            onClick={() => header.node_id && navigate(`/daily-reports/open?node_id=${header.node_id}`)}>
            今日の日報を書く →
          </button>
        </div>
      )}

      {submitErrors.length > 0 && (
        <div className="mt-3 bg-danger-bg border border-danger-border rounded-lg px-3 py-2.5">
          <p className="text-body text-danger-text font-semibold">✕ 提出できません ({submitErrors.length}件)</p>
          <ul className="mt-1.5 space-y-1">
            {submitErrors.map((d, i) => (
              // eslint-disable-next-line react/no-array-index-key -- 順序保証のみ、識別子は無い
              <li key={i} className="text-caption text-danger-text flex items-center justify-between gap-2">
                <span>・{d.message}</span>
                {d.field && fieldRefs.current.has(d.field) && (
                  <button type="button" className="underline underline-offset-2 shrink-0 min-h-[44px] px-1 inline-flex items-center"
                    onClick={() => { const el = fieldRefs.current.get(d.field!); el?.scrollIntoView({ behavior: 'smooth', block: 'center' }); el?.focus(); }}>
                    移動
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {isDraft && (
        <div className="mt-3 text-caption text-text-sub flex items-center gap-1.5">
          {autosave.status === 'saving' && <><Loader2 size={12} className="animate-spin" aria-hidden />保存中…</>}
          {autosave.status === 'saved' && autosave.lastSavedAt && <>保存済み {autosave.lastSavedAt}</>}
          {autosave.status === 'offline' && <span className="text-alert-text">オフライン・再接続を待っています</span>}
          {autosave.status === 'error' && <span className="text-alert-text">保存でエラーが発生しました</span>}
          {autosave.status === 'idle' && <>&nbsp;</>}
        </div>
      )}

      {maskedCount > 0 && (
        <p className="mt-3 text-caption text-text-sub">非公開の項目があります ({maskedCount}件)</p>
      )}

      {sections.map((section, sIdx) => (
        // eslint-disable-next-line react/no-array-index-key -- section_label はnull許容で一意キーにならない
        <div key={sIdx} className="mt-4">
          {section.label && <h2 className="text-section text-text-section uppercase mb-1">{section.label}</h2>}
          <div className="divide-y divide-surface-line border-t border-b border-surface-line">
            {section.items.map((f) => {
              if (f.value_kind === 'line_items') {
                const options = parseLineItemFieldOptions(f.field_options_json);
                const rows = lineItemsByField.get(f.field_definition_id) ?? [];
                return (
                  <div key={f.field_definition_id} className="py-3" ref={(el) => { if (el) fieldRefs.current.set(f.field_definition_id, el); }} tabIndex={-1}>
                    <label className="text-label text-text-sub">{f.label}{f.visibility === 'required' && <span className="ml-0.5">*</span>}</label>
                    <div className="mt-1.5">
                      {options?.line_item_kind === 'denomination' ? (
                        <DenominationLineItemsSection
                          field={f} rows={rows} denominations={denominationsQuery.data ?? []} disabled={!isDraft}
                          onRowsChange={(next) => autosave.queueLineItems(f.field_definition_id, next)}
                        />
                      ) : options?.line_item_kind === 'general' ? (
                        <GeneralLineItemsSection
                          fieldOptions={options} rows={rows} disabled={!isDraft}
                          onRowsChange={(next) => autosave.queueLineItems(f.field_definition_id, next)}
                        />
                      ) : (
                        <p className="text-body text-text-sub">この項目の設定が不正です。</p>
                      )}
                    </div>
                    {errorByField.get(f.field_definition_id) && <p role="alert" className="mt-1 text-caption text-danger-text">{errorByField.get(f.field_definition_id)}</p>}
                  </div>
                );
              }
              if (f.value_kind === 'attachment') {
                const fieldAttachments = attachments.filter((a) => a.field_definition_id === f.field_definition_id);
                const requiredCount = inferAttachmentRequiredCount(f.field_options_json);
                // 2026-09-09 (user Q3): 必要枚数が1件 (既定・大多数) のときは従来どおり「最新1件+差し替え」
                // のシンプルなUXを維持する (§8-134型の不要な変更を避ける)。2件以上を要求する項目だけ、
                // 一覧+個別削除+追加の複数枚UIに切り替える。
                if (requiredCount <= 1) {
                  const latest = fieldAttachments[fieldAttachments.length - 1];
                  return (
                    <div key={f.field_definition_id} className="py-3" ref={(el) => { if (el) fieldRefs.current.set(f.field_definition_id, el); }} tabIndex={-1}>
                      <label className="text-label text-text-sub">{f.label}{f.visibility === 'required' && <span className="ml-0.5">*</span>}</label>
                      <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                        {latest ? (
                          <>
                            <span className="text-body text-text-body flex items-center gap-1.5">
                              {latest.display_level === 'full' && latest.ocr?.confirmed_at ? <CheckCircle2 size={16} className="text-success-text" aria-hidden /> : <Camera size={16} className="text-text-sub" aria-hidden />}
                              {latest.display_level === 'full' ? (latest.ocr?.confirmed_at ? '確認済み' : '未確認') : '非公開'}
                            </span>
                            <Button variant="secondary" size="sm" disabled={!isDraft}
                              onClick={() => navigate(`/daily-reports/${id}/attachments/${f.field_definition_id}?attachment_id=${latest.attachment_id}`)}>
                              差し替える
                            </Button>
                          </>
                        ) : (
                          <Button variant="secondary" size="sm" leftIcon={<Camera size={14} />} disabled={!isDraft}
                            onClick={() => navigate(`/daily-reports/${id}/attachments/${f.field_definition_id}`)}>
                            写真を追加
                          </Button>
                        )}
                      </div>
                      {errorByField.get(f.field_definition_id) && <p role="alert" className="mt-1 text-caption text-danger-text">{errorByField.get(f.field_definition_id)}</p>}
                    </div>
                  );
                }
                const satisfied = fieldAttachments.length >= requiredCount;
                return (
                  <div key={f.field_definition_id} className="py-3" ref={(el) => { if (el) fieldRefs.current.set(f.field_definition_id, el); }} tabIndex={-1}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <label className="text-label text-text-sub">{f.label}{f.visibility === 'required' && <span className="ml-0.5">*</span>}</label>
                      {/* design-expertレビュー Major是正: 未充足は提出をブロックする条件 (daily-reports.ts の
                          ATTACHMENT_REQUIRED) のため danger.* を使う (§2.2、alert.*は警告のみ)。 */}
                      <span className={`text-caption ${satisfied ? 'text-text-sub' : 'text-danger-text'}`}>{fieldAttachments.length}/{requiredCount}枚</span>
                    </div>
                    <ul className="mt-1.5 space-y-1.5">
                      {fieldAttachments.map((a, i) => (
                        <li key={a.attachment_id} className="flex items-center gap-2 flex-wrap">
                          <button type="button" className="text-body text-text-body flex items-center gap-1.5 min-h-[44px] underline-offset-2 hover:underline"
                            onClick={() => navigate(`/daily-reports/${id}/attachments/${f.field_definition_id}?attachment_id=${a.attachment_id}`)}>
                            {a.display_level === 'full' && a.ocr?.confirmed_at ? <CheckCircle2 size={16} className="text-success-text" aria-hidden /> : <Camera size={16} className="text-text-sub" aria-hidden />}
                            {i + 1}枚目 {a.display_level === 'full' ? (a.ocr?.confirmed_at ? '確認済み' : '未確認') : '非公開'}
                          </button>
                          {isDraft && (
                            <button type="button" aria-label={`${i + 1}枚目を削除`}
                              className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-text-sub hover:text-danger-text focus-ring"
                              onClick={() => setDeleteAttachmentTarget(a.attachment_id)}>
                              <Trash2 size={16} aria-hidden />
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                    {isDraft && (
                      <Button variant="secondary" size="sm" leftIcon={<Camera size={14} />} className="mt-1.5"
                        onClick={() => navigate(`/daily-reports/${id}/attachments/${f.field_definition_id}`)}>
                        写真を追加
                      </Button>
                    )}
                    {errorByField.get(f.field_definition_id) && <p role="alert" className="mt-1 text-caption text-danger-text">{errorByField.get(f.field_definition_id)}</p>}
                  </div>
                );
              }
              return (
                <DailyReportFieldRow
                  key={f.field_definition_id}
                  field={f}
                  carryoverHint={carryoverHintsRef.current.get(f.field_definition_id) ?? null}
                  disabled={!isDraft}
                  errorMessage={errorByField.get(f.field_definition_id)}
                  fieldRef={(el) => { if (el) fieldRefs.current.set(f.field_definition_id, el); }}
                  onValueChange={(value, isNotApplicable) => handleFieldChange(f, value, isNotApplicable)}
                />
              );
            })}
          </div>
        </div>
      ))}

      {header.node_id && header.business_date && (
        <DailyReportWorkShiftsSection nodeId={header.node_id} businessDate={header.business_date} />
      )}

      {!isDraft && (
        <DailyReportReconciliationsSection
          reconciliations={reconciliations}
          reasons={reasonsQuery.data ?? []}
          disabled={false}
          onAccepted={reloadFromServer}
          reportId={id}
        />
      )}

      {/* §8-177対応でmainに will-change-transform を付けたため、position:fixed はmain基準になり
          スクロール時に位置がズレる (2026-09-24 user報告)。ヘッダー (上のsticky top-0) と同じ
          sticky bottom-0 に統一する (mainがoverflow-y-autoの独立スクロール領域のため、これが正しい)。 */}
      {isDraft && (
        <div className="sticky bottom-0 -mx-4 bg-surface-card border-t border-surface-border px-4 py-3 flex gap-2 justify-end z-20">
          <Button variant="secondary" onClick={() => { void autosave.flushNow(); toast.success('保存しました'); }} disabled={submitting}>
            下書きを保存
          </Button>
          <Button variant="primary" onClick={() => setShowSubmitPreview(true)}>
            提出する
          </Button>
        </div>
      )}

      <DailyReportSubmitPreview
        open={showSubmitPreview} onClose={() => setShowSubmitPreview(false)}
        onConfirm={() => { void handleSubmit(); }} submitting={submitting}
        sections={sections} lineItemsByField={lineItemsByField} attachments={attachments}
        denominations={denominationsQuery.data ?? []}
      />

      <Modal open={!!ocrOverrideTarget} title="OCR結果を上書きします" onClose={() => setOcrOverrideTarget(null)} size="sm"
        footer={(
          <>
            <Button variant="secondary" onClick={() => setOcrOverrideTarget(null)}>キャンセル</Button>
            <Button variant="primary" onClick={confirmOcrOverride}>上書きする</Button>
          </>
        )}>
        <div className="space-y-2">
          <p className="text-body text-text-body">この項目はOCRで読み取った値です。上書きするには理由が必要です。</p>
          <textarea className={`${inputCls} h-24`} value={ocrOverrideReason} onChange={(e) => setOcrOverrideReason(e.target.value)}
            placeholder="例: 写真がブレて読み取り値が誤っていたため" />
        </div>
      </Modal>

      {/* design-expertレビュー是正: 破壊的操作のため素の Modal ではなく ConfirmDialog の
          destructive 表現 (Cancel自動フォーカス・EnvironmentBadge) を使う。理由入力は
          message (ReactNode) に載せ、未入力の間は confirmDisabled で確定を防ぐ。 */}
      <ConfirmDialog open={reopenModalOpen} title="下書きに差し戻しますか？" onClose={() => setReopenModalOpen(false)}
        destructive confirmLabel="差し戻す" loading={reopening}
        confirmDisabled={!reopenReason.trim()} onConfirm={() => { void handleReopen(); }}
        message={(
          <div className="space-y-2">
            <p>提出済みのこの日報を下書きに戻し、記入者が再度編集・再提出できるようにします。理由の記録が必要です。</p>
            <textarea className={`${inputCls} h-24`} value={reopenReason} onChange={(e) => setReopenReason(e.target.value)}
              placeholder="例: 売上総額の入力誤りが見つかったため" />
          </div>
        )}
      />

      <ConfirmDialog open={!!deleteAttachmentTarget} title="この写真を削除しますか？" onClose={() => setDeleteAttachmentTarget(null)}
        destructive confirmLabel="削除する" loading={deletingAttachment} onConfirm={() => { void handleDeleteAttachment(); }}
        message="削除すると、この写真とOCRの読み取り結果は失われます。再度添付する場合は撮り直しが必要です。"
      />
    </div>
  );
}

/** user指摘 (2026-09-09)「提出前に入力内容を一覧で確認したい」への対応。項目定義設定画面
 *  (`DailyReportFieldsSettingsPage.tsx` の `FieldPreview`) と同じ考え方で、専用の表示ロジックを
 *  新設せず、メイン画面が実際に使う `DailyReportFieldRow`/`DenominationLineItemsSection`/
 *  `GeneralLineItemsSection` を `disabled` で再利用する (§8-134型のミラーずれ回避)。 */
function DailyReportSubmitPreview({
  open, onClose, onConfirm, submitting, sections, lineItemsByField, attachments, denominations,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  submitting: boolean;
  sections: { label: string | null; items: DailyReportField[] }[];
  lineItemsByField: Map<string, DailyReportLineItemRow[]>;
  attachments: DailyReportAttachment[];
  denominations: Denomination[];
}) {
  if (!open) return null;
  return (
    <Modal
      open title="この内容で提出しますか？" onClose={onClose} size="lg"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>戻って修正する</Button>
          <Button variant="primary" loading={submitting} loadingLabel="提出中…" onClick={onConfirm}>この内容で提出する</Button>
        </>
      )}
    >
      <p className="text-caption text-text-sub mb-3">
        提出すると内容が確定します。提出後にOCR由来の値を変更する場合は理由の入力が必要になります。
      </p>
      {sections.map((section, sIdx) => (
        // eslint-disable-next-line react/no-array-index-key -- section_label はnull許容で一意キーにならない (メイン画面の一覧と同じ理由)
        <div key={sIdx} className="mt-4 first:mt-0">
          {section.label && <h3 className="text-section text-text-section uppercase mb-1">{section.label}</h3>}
          <div className="divide-y divide-surface-line border-t border-b border-surface-line">
            {section.items.map((f) => {
              if (f.value_kind === 'line_items') {
                const options = parseLineItemFieldOptions(f.field_options_json);
                const rows = lineItemsByField.get(f.field_definition_id) ?? [];
                return (
                  <div key={f.field_definition_id} className="py-3">
                    <label className="text-label text-text-sub">{f.label}</label>
                    {/* design-expertレビュー Critical是正: 明細行1行の実寸がメイン画面より狭い
                        モーダル内padding分だけオーバーフローしうる (320/375px幅)。行自体の幅は
                        変えず、横スクロールをこの内側だけに閉じ込めてページ全体の横スクロールを防ぐ。 */}
                    <div className="mt-1.5 overflow-x-auto">
                      {options?.line_item_kind === 'denomination' ? (
                        <DenominationLineItemsSection field={f} rows={rows} denominations={denominations} disabled onRowsChange={() => {}} />
                      ) : options?.line_item_kind === 'general' ? (
                        <GeneralLineItemsSection fieldOptions={options} rows={rows} disabled onRowsChange={() => {}} />
                      ) : (
                        <p className="text-body text-text-sub">この項目の設定が不正です。</p>
                      )}
                    </div>
                  </div>
                );
              }
              if (f.value_kind === 'attachment') {
                const fieldAttachments = attachments.filter((a) => a.field_definition_id === f.field_definition_id);
                return (
                  <div key={f.field_definition_id} className="py-3">
                    <label className="text-label text-text-sub">{f.label}</label>
                    {/* design-expertレビュー Major是正: 枚数のみだと本画面にある「OCR未確認」の
                        見落としリスクをそのまま提出前最終確認に持ち込んでしまう。本画面と同じ
                        ラベル (確認済み/未確認/非公開) を1枚ずつ出す。 */}
                    {fieldAttachments.length > 0 ? (
                      <ul className="mt-1 space-y-0.5">
                        {fieldAttachments.map((a, i) => (
                          <li key={a.attachment_id} className="text-body text-text-body">
                            {i + 1}枚目: {a.display_level === 'full' ? (a.ocr?.confirmed_at ? '確認済み' : '未確認') : '非公開'}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="mt-1 text-body text-text-sub">添付なし</p>
                    )}
                  </div>
                );
              }
              return <DailyReportFieldRow key={f.field_definition_id} field={f} disabled onValueChange={() => {}} />;
            })}
          </div>
        </div>
      ))}
    </Modal>
  );
}

function DailyReportReconciliationsSection({
  reconciliations, reasons, disabled, onAccepted, reportId,
}: {
  reconciliations: DailyReportReconciliation[];
  reasons: { reason_id: string; label: string; is_active: boolean }[];
  disabled: boolean;
  onAccepted: () => Promise<void>;
  reportId: string;
}) {
  const [acceptTarget, setAcceptTarget] = useState<string | null>(null);
  const [reasonId, setReasonId] = useState<string>('');
  const [reasonText, setReasonText] = useState('');
  const [accepting, setAccepting] = useState(false);

  if (reconciliations.length === 0) return null;

  const submitAccept = async () => {
    if (!acceptTarget) return;
    setAccepting(true);
    try {
      const res = await acceptDailyReportReconciliation(reportId, acceptTarget, {
        reason: reasonText.trim() || undefined,
        reason_id: reasonId || undefined,
      });
      toast.success('差異を受け入れました');
      if (res.consecutive_same_reason_days >= 3) {
        toast.warning(`過去${res.consecutive_same_reason_days}日連続で同じ理由です`);
      }
      setAcceptTarget(null);
      setReasonId('');
      setReasonText('');
      await onAccepted();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : '受け入れに失敗しました');
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div className="mt-4">
      <h2 className="text-section text-text-section uppercase mb-1">突合結果</h2>
      <div className="divide-y divide-surface-line border-t border-b border-surface-line">
        {reconciliations.map((r) => {
          const isMasked = r.left_value === null && r.right_value === null && r.difference === null && r.verdict !== 'not_applicable';
          return (
            <div key={r.reconciliation_id} className="py-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="text-body text-text-body">{r.label ?? '(ルール不明)'}</span>
                {r.verdict === 'ok' && <span className="text-caption text-success-text flex items-center gap-1"><CheckCircle2 size={14} aria-hidden />一致</span>}
                {r.verdict === 'not_applicable' && <span className="text-caption text-text-sub">評価対象外</span>}
                {r.verdict === 'ng' && (
                  <span className={`text-caption flex items-center gap-1 ${r.on_ng === 'block' ? 'text-danger-text' : 'text-alert-text'}`}>
                    <AlertTriangle size={14} aria-hidden />
                    不一致{!isMasked && r.difference !== null ? ` 差額 ${r.difference >= 0 ? '+' : ''}¥${r.difference.toLocaleString()}` : isMasked ? ' (金額は非公開)' : ''}
                  </span>
                )}
              </div>
              {r.verdict === 'ng' && (
                r.accepted_at ? (
                  <p className="mt-1 text-caption text-text-sub">受け入れ済み{r.accept_reason ? `: ${r.accept_reason}` : ''}</p>
                ) : (
                  <div className="mt-1.5">
                    <Button variant="secondary" size="sm" disabled={disabled} onClick={() => setAcceptTarget(r.reconciliation_id)}>
                      この差異を受け入れる
                    </Button>
                  </div>
                )
              )}
              {r.consecutive_same_reason_days != null && r.consecutive_same_reason_days >= 3 && (
                <p className="mt-1 text-caption text-alert-text flex items-center gap-1"><AlertTriangle size={12} aria-hidden />過去{r.consecutive_same_reason_days}日連続で同じ理由です</p>
              )}
            </div>
          );
        })}
      </div>

      <Modal open={!!acceptTarget} title="差異を受け入れる" onClose={() => setAcceptTarget(null)} size="sm"
        footer={(
          <>
            <Button variant="secondary" onClick={() => setAcceptTarget(null)}>キャンセル</Button>
            <Button variant="primary" loading={accepting} onClick={() => { void submitAccept(); }}>受け入れる</Button>
          </>
        )}>
        <div className="space-y-3">
          <div>
            <label className="block text-caption text-text-sub mb-1">定型理由</label>
            <Select value={reasonId} onValueChange={setReasonId}>
              <SelectTrigger aria-label="定型理由"><SelectValue placeholder="選択してください (任意)" /></SelectTrigger>
              <SelectContent>
                {reasons.filter((r) => r.is_active).map((r) => <SelectItem key={r.reason_id} value={r.reason_id}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-caption text-text-sub mb-1">補足 (自由記述)</label>
            <textarea className={`${inputCls} h-20`} value={reasonText} onChange={(e) => setReasonText(e.target.value)} />
          </div>
        </div>
      </Modal>
    </div>
  );
}
